import { useEffect, useRef } from 'react';
import { useU2000WebSocketContext } from '../../../../u2000Core/context/websocket/U2000WebSocketContext';
import {
  isNotificationMessage,
  U3141_RECONSTITUTE_CHAIN_FROM_PDF_JOB_TYPE,
} from '../../../../u2000Core/types/U2000WebSocketMessages';

export type U3141ReconstituteChainFromPdfNotificationPayload = {
  masterLotChainId: string;
  alerteCount: number;
  ficheCount?: number;
  failed?: boolean;
  message?: string;
};

/**
 * Écoute les notifications WebSocket "notification:new" pour le job reconstitution chaîne PDF.
 * Lorsque metadata.jobType === U3141_RECONSTITUTE_CHAIN_FROM_PDF
 * et metadata.masterLotChainId correspond au lot courant, appelle onCompleted.
 */
export function useU3141ReconstituteChainFromPdfNotification(
  masterLotChainId: string | undefined,
  onCompleted: (payload: U3141ReconstituteChainFromPdfNotificationPayload) => void
) {
  const { on, off } = useU2000WebSocketContext();
  const onCompletedRef = useRef(onCompleted);
  onCompletedRef.current = onCompleted;

  useEffect(() => {
    if (!masterLotChainId) return;

    const handleNotification = (message: unknown) => {
      if (!isNotificationMessage(message)) return;

      const metadata = message.metadata as Record<string, unknown> | undefined;
      const jobType = metadata?.jobType as string | undefined;
      const masterLotChainIdFromMsg = metadata?.masterLotChainId as string | undefined;

      if (jobType !== U3141_RECONSTITUTE_CHAIN_FROM_PDF_JOB_TYPE || masterLotChainIdFromMsg !== masterLotChainId) {
        return;
      }

      const alerteRaw = metadata?.alerteCount;
      const alerteCount = typeof alerteRaw === 'number' ? alerteRaw : Number(alerteRaw) || 0;
      const ficheRaw = metadata?.ficheCount;
      const ficheCount = typeof ficheRaw === 'number' ? ficheRaw : undefined;

      if (import.meta.env.DEV) {
        console.log('[U3141 Reconstitute chain PDF] Notification reçue, masterLotChainId:', masterLotChainId, {
          alerteCount,
          ficheCount,
        });
      }

      onCompletedRef.current({
        masterLotChainId,
        alerteCount,
        ficheCount,
        failed: message.importance === 'error' || message.importance === 'critical',
        message: message.message,
      });
    };

    on('notification:new', handleNotification);
    return () => off('notification:new', handleNotification);
  }, [masterLotChainId, on, off]);
}
